import {
  Autocomplete,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
  Collapse,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
} from "@mui/material"
import { useEffect, useState } from "react"
import useAppStore from "../../../store/app"

import { ExpandLess, ExpandMore } from "@mui/icons-material"
import useTranslation from "../../../hooks/use_translation"
import SecondaryButton from "../buttons/SecondaryButton"
import PrimaryButton from "../buttons/PrimaryButton"

const Loading = () => {
  const { pages } = useTranslation()

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <Typography variant="h6" sx={{ mb: 4 }}>
        {pages?.loading}
      </Typography>
      <CircularProgress color="inherit" />
    </Box>
  )
}

const BodyPartSearch = () => {
  const [symptoms, setSymptoms] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState({})

  const { selected } = useAppStore((state) => state.anamnesis)
  const { bodyPart } = useAppStore((state) => state.anamnesis)
  const searchSymptomsByBodyPart = useAppStore((state) => state.searchSymptomsByBodyPart)
  const createSymptom = useAppStore((state) => state.createSymptom)
  const getBodyPart = useAppStore((state) => state.getBodyPart)
  const toggleDrawer = useAppStore((state) => state.toggleDrawer)

  useEffect(() => {
    setLoading(true)
    getBodyPart(selected)

    searchSymptomsByBodyPart(selected).then((response) => {
      setSymptoms(response.data)
      setLoading(false)
    })
  }, [selected])

  const handleCollapse = (id) => {
    setOpen((prevOpen) => ({
      ...prevOpen,
      [id]: !prevOpen[id],
    }))
  }

  if (loading) {
    return <Loading />
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ ml: 2, backgroundColor: "bg.light", fontWeight: 700 }}>
        {bodyPart?.name}
      </Typography>

      <Divider sx={{ my: 1 }} />

      <List>
        {symptoms.map((item, i) => (
          <Box key={i}>
            <ListItemButton>
              <ListItemText
                primary={item.name}
                onClick={() => {
                  const data = {
                    symptoms: [{ id: item.id, active: true }],
                  }

                  createSymptom(data).then(() => {
                    setSymptoms([])
                    toggleDrawer(false)
                  })
                }}
              />

              {item.images.length > 0 && (
                <IconButton onClick={() => handleCollapse(`item${i}`)}>
                  {/*<FilterIcon />*/}
                  {open[`item${i}`] ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </ListItemButton>
            {item.images.length > 0 && (
              <Collapse in={open[`item${i}`]} timeout="auto" unmountOnExit>
                {item.images.map((image, n) => (
                  <Box
                    component="img"
                    key={n}
                    src={image.url}
                    sx={{ m: 2, width: "300px", borderRadius: "20px" }}
                  />
                ))}
              </Collapse>
            )}
          </Box>
        ))}
      </List>
    </Box>
  )
}

const TextSearch = () => {
  const [symptoms, setSymptoms] = useState([])
  const [value, setValue] = useState("")
  const [symptom, setSymptom] = useState("")
  // const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState({})

  const searchSymptoms = useAppStore((state) => state.searchSymptoms)
  const createSymptom = useAppStore((state) => state.createSymptom)
  const toggleDrawer = useAppStore((state) => state.toggleDrawer)

  const { anamnesis } = useTranslation()

  const handleCollapse = (id) => {
    setOpen((prevOpen) => ({
      ...prevOpen,
      [id]: !prevOpen[id],
    }))
  }

  // if (loading) {
  //   return <Loading />
  // }

  return (
    <Autocomplete
      id="symptoms"
      options={symptoms}
      value={symptom}
      inputValue={value}
      noOptionsText={anamnesis?.no_options}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.name || ""}
      clearOnBlur={true}
      disableClearable={true}
      sx={{
        mt: 4,
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px", // Custom border radius
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          placeholder={anamnesis?.symptoms_search_hint}
          variant="outlined"
          sx={{ backgroundColor: "white", borderRadius: "8px" }}
        />
      )}
      renderOption={(props, option) => (
        <Box key={option.id}>
          <ListItemButton>
            <ListItemText
              primary={option.name}
              onClick={() => {
                const data = {
                  symptoms: [{ id: option.id, active: true }],
                }

                createSymptom(data).then(() => {
                  setSymptoms([])
                  setSymptom("")
                  setValue("")
                  toggleDrawer(false)
                })
              }}
            />

            {option.images.length > 0 && (
              <IconButton onClick={() => handleCollapse(`item${option.id}`)}>
                {open[`item${option.id}`] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </ListItemButton>
          {option.images.length > 0 && (
            <Collapse in={open[`item${option.id}`]} timeout="auto" unmountOnExit>
              {option.images.map((image, n) => (
                <Box
                  component="img"
                  key={n}
                  src={image.url}
                  sx={{ m: 2, width: "300px", borderRadius: "20px" }}
                />
              ))}
            </Collapse>
          )}
        </Box>
      )}
      onInputChange={(event, newInputValue) => {
        setValue(newInputValue)

        searchSymptoms(newInputValue)
          .then((response) => response.data)
          .then((data) => {
            setSymptoms(data)
          })
      }}
    />
  )
}

const AiSearch = () => {
  const [symptoms, setSymptoms] = useState([])
  const [selectedSymptoms, setSelectedSymptoms] = useState([])
  const [value, setValue] = useState("")
  const [open, setOpen] = useState({})

  const searchSymptomsByAi = useAppStore((state) => state.searchSymptomsByAi)
  const createSymptom = useAppStore((state) => state.createSymptom)
  const toggleDrawer = useAppStore((state) => state.toggleDrawer)
  const { direction } = useAppStore((state) => state.meta)

  const { anamnesis } = useTranslation()

  const handleSearch = () => {
    searchSymptomsByAi(value)
      .then((response) => response.data)
      .then((data) => {
        setSymptoms(data)
      })
  }

  const handleCollapse = (id) => {
    setOpen((prevOpen) => ({
      ...prevOpen,
      [id]: !prevOpen[id],
    }))
  }

  const handleCreateSymptoms = () => {
    const data = {
      symptoms: selectedSymptoms,
    }

    createSymptom(data).then(() => {
      setSymptoms([])
      setSelectedSymptoms([])
      toggleDrawer(false)
    })
  }

  const handleToggle = (item) => {
    setSelectedSymptoms((prev) => {
      const exists = prev.find((s) => s.id === item.id)
      return exists
        ? prev.filter((s) => s.id !== item.id)
        : [...prev, { id: item.id, active: true }]
    })
  }

  return (
    <Box>
      <TextField
        multiline
        fullWidth
        rows={4}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
        placeholder={anamnesis?.symptoms_search_hint}
        variant="outlined"
        sx={{ backgroundColor: "white", borderRadius: "8px", mt: 4 }}
      />
      <Stack direction={direction === "rtl" ? "row-reverse" : "row"} columnGap={2}>
        <PrimaryButton
          variant="outlined"
          text={anamnesis?.symptoms_search_by_ai_clear}
          sx={{ mt: 2, px: 4 }}
          onClick={() => setValue("")}
        />
        <SecondaryButton
          text={anamnesis?.symptoms_search_by_ai_submit}
          sx={{ mt: 2, px: 4 }}
          onClick={handleSearch}
        />
      </Stack>

      <List sx={{ mb: 10 }}>
        {symptoms.map((item, i) => (
          <Box key={i}>
            <ListItemButton
              onClick={() => handleToggle(item)}
              selected={selectedSymptoms.some((s) => s.id === item.id)}
            >
              <ListItemText primary={item.name} />

              {item.images.length > 0 && (
                <IconButton onClick={() => handleCollapse(`item${i}`)}>
                  {/*<FilterIcon />*/}
                  {open[`item${i}`] ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </ListItemButton>
            {item.images.length > 0 && (
              <Collapse in={open[`item${i}`]} timeout="auto" unmountOnExit>
                {item.images.map((image, n) => (
                  <Box
                    component="img"
                    key={n}
                    src={image.url}
                    sx={{ m: 2, width: "300px", borderRadius: "20px" }}
                  />
                ))}
              </Collapse>
            )}
          </Box>
        ))}
      </List>

      {symptoms.length > 0 && (
        <PrimaryButton
          sx={{ position: "absolute", bottom: "65px", right: "20px" }}
          onClick={handleCreateSymptoms}
          text={anamnesis?.symptoms_search_by_ai_select}
        />
      )}
    </Box>
  )
}

const SymptomsSearch = () => {
  const { selected } = useAppStore((state) => state.anamnesis)
  const { direction } = useAppStore((state) => state.meta)

  const [searchType, setSearchType] = useState("ai")

  const { anamnesis } = useTranslation()

  const handleChangeSearchType = (event, newSearchType) => {
    if (newSearchType !== null) {
      setSearchType(newSearchType)
    }
  }

  if (selected !== null) {
    return <BodyPartSearch />
  }

  return (
    <Box>
      <ToggleButtonGroup
        color="primary"
        dir={direction}
        value={searchType}
        exclusive
        onChange={handleChangeSearchType}
        aria-label="SearchType"
        sx={{
          width: "100%",
          display: "flex",
          "& .MuiToggleButtonGroup-grouped": {
            flex: 1,
          },
        }}
      >
        <ToggleButton
          value="ai"
          sx={{
            borderRadius: 2.5,
            backgroundColor: "gray.main",
            "&.Mui-selected": {
              backgroundColor: "primary_light.main",
              color: "white",
              "&:hover": {
                backgroundColor: "primary_light.dark",
              },
            },
          }}
        >
          <Typography variant="button" sx={{ fontWeight: 700 }}>
            {anamnesis?.symptoms_search_by_ai}
          </Typography>
        </ToggleButton>
        <ToggleButton
          value="base"
          sx={{
            borderRadius: 2.5,
            backgroundColor: "gray.main",
            "&.Mui-selected": {
              backgroundColor: "primary_light.main",
              color: "white",
              "&:hover": {
                backgroundColor: "primary_light.dark",
              },
            },
          }}
        >
          <Typography variant="button" sx={{ fontWeight: 700 }}>
            {anamnesis?.symptoms_search_by_text}
          </Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      {searchType === "ai" && <AiSearch />}
      {searchType === "base" && <TextSearch />}
    </Box>
  )
}

export default SymptomsSearch
